/**
 * CampaignDispatchModal
 *
 * Modal de confirmação antes do disparo de campanha.
 * Mostra: status WhatsApp, audiência estimada, quota do dia/mês e
 * opções "Testar (sem envio real)" vs "Disparar agora".
 */

import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import {
  X, MessageCircle, Mail, Smartphone,
  CheckCircle2, AlertTriangle, Loader2,
  Send, FlaskConical, Users,
} from 'lucide-react';
import { usePreflightCheck } from '../hooks/useSendCampaign';
import type { Campaign } from '../types';

interface CampaignDispatchModalProps {
  campaign:      Campaign;
  audienceCount: number;
  onConfirm:     (testMode: boolean) => void;
  onClose:       () => void;
}

export function CampaignDispatchModal({
  campaign, audienceCount, onConfirm, onClose,
}: CampaignDispatchModalProps) {
  const isWhatsApp = campaign.type === 'whatsapp';

  const { data: preflight, isLoading } = usePreflightCheck(isWhatsApp);

  const waActive    = preflight?.waStatus === 'active';
  const overQuota   = preflight
    ? (preflight.sentToday + audienceCount) > preflight.dailyLimit
    : false;
  const overMonthly = preflight
    ? (preflight.sentMonth + audienceCount) > preflight.monthlyLimit
    : false;
  const canSendReal = isWhatsApp && waActive && !overQuota && !overMonthly;

  const TypeIcon = isWhatsApp ? MessageCircle
    : campaign.type === 'email' ? Mail
    : Smartphone;

  return createPortal(
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-lg rounded-3xl card-premium shadow-deep animate-fade-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-hairline">
          <div className="flex items-center gap-2">
            <Send className="h-4 w-4 text-accent" />
            <span className="font-display text-base font-bold text-ink">Confirmar disparo</span>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-ink-muted hover:text-ink hover:bg-white/5 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* ── Campaign preview ── */}
          <div className="flex items-start gap-3 rounded-xl bg-midnight-2/60 border border-hairline px-4 py-3">
            <span className="mt-0.5 rounded-lg bg-midnight-4 p-2 shrink-0">
              <TypeIcon className="h-4 w-4 text-ink-muted" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-ink truncate">{campaign.name}</p>
              {campaign.message_body && (
                <p className="text-xs text-ink-muted mt-0.5 line-clamp-2 leading-relaxed">
                  {campaign.message_body.slice(0, 120)}
                  {campaign.message_body.length > 120 ? '…' : ''}
                </p>
              )}
            </div>
          </div>

          {/* ── Audience count ── */}
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 shrink-0 rounded-xl bg-accent/10 border border-accent/20 flex items-center justify-center">
              <Users className="h-4 w-4 text-accent" />
            </div>
            <div>
              <p className="text-xs text-ink-muted">Audiência estimada</p>
              <p className="text-2xl font-extrabold font-display text-white leading-tight">
                {audienceCount.toLocaleString('pt-BR')}
                <span className="text-sm font-normal text-ink-muted ml-1.5">contatos</span>
              </p>
            </div>
          </div>

          {/* ── WhatsApp section ── */}
          {isWhatsApp && (
            <>
              {isLoading ? (
                <div className="flex items-center gap-2 text-ink-subtle text-sm">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Verificando integração WhatsApp…</span>
                </div>
              ) : (
                <div className={`flex items-center gap-3 rounded-xl px-4 py-3 border ${
                  waActive
                    ? 'bg-emerald-500/8 border-emerald-500/20'
                    : 'bg-amber-500/8 border-amber-500/20'
                }`}>
                  {waActive
                    ? <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" />
                    : <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0" />
                  }
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-semibold ${waActive ? 'text-emerald-300' : 'text-amber-300'}`}>
                      {waActive ? 'WhatsApp conectado' : 'WhatsApp não configurado'}
                    </p>
                    {waActive && preflight?.waPhoneNumber && (
                      <p className="text-xs text-ink-subtle font-mono mt-0.5">{preflight.waPhoneNumber}</p>
                    )}
                    {!waActive && (
                      <p className="text-xs text-ink-muted mt-0.5">
                        O disparo será simulado em modo teste.{' '}
                        <Link
                          to="/app/configuracoes/whatsapp"
                          onClick={onClose}
                          className="text-accent underline underline-offset-2"
                        >
                          Configurar WhatsApp →
                        </Link>
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Quota bars */}
              {preflight && (
                <div className="space-y-3">
                  <QuotaBar
                    label="Enviadas hoje"
                    used={preflight.sentToday}
                    limit={preflight.dailyLimit}
                    willSend={audienceCount}
                    overQuota={overQuota}
                  />
                  <QuotaBar
                    label="Enviadas no mês"
                    used={preflight.sentMonth}
                    limit={preflight.monthlyLimit}
                    willSend={audienceCount}
                    overQuota={overMonthly}
                  />
                </div>
              )}

              {/* Over-quota warning */}
              {(overQuota || overMonthly) && (
                <div className="flex items-start gap-2 rounded-xl bg-danger/8 border border-danger/20 px-3 py-2.5">
                  <AlertTriangle className="h-4 w-4 text-danger shrink-0 mt-0.5" />
                  <p className="text-xs text-danger leading-relaxed">
                    {overQuota
                      ? `Enviar ${audienceCount.toLocaleString('pt-BR')} mensagens ultrapassaria o limite diário do plano.`
                      : `Enviar ${audienceCount.toLocaleString('pt-BR')} mensagens ultrapassaria o limite mensal do plano.`
                    }
                    {' '}O modo teste ainda está disponível.
                  </p>
                </div>
              )}
            </>
          )}

          {/* ── Non-WhatsApp notice ── */}
          {!isWhatsApp && (
            <div className="flex items-start gap-2 rounded-xl bg-amber-500/8 border border-amber-500/20 px-3 py-2.5">
              <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
              <p className="text-xs text-amber-300 leading-relaxed">
                Campanhas de {campaign.type === 'email' ? 'e-mail' : 'SMS'} estão em desenvolvimento.
                Apenas o modo de teste está disponível nesta versão.
              </p>
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="flex items-center gap-3 px-6 pb-6">
          <button
            onClick={() => onConfirm(true)}
            className="flex-1 flex items-center justify-center gap-2 rounded-xl btn-ghost px-4 py-3 text-sm font-semibold"
          >
            <FlaskConical className="h-4 w-4" />
            <span>Testar</span>
          </button>

          <button
            onClick={() => onConfirm(false)}
            disabled={!canSendReal}
            title={
              !canSendReal
                ? !isWhatsApp
                  ? 'Canal não disponível nesta versão'
                  : !waActive
                  ? 'WhatsApp não está configurado'
                  : 'Limite de mensagens do plano atingido'
                : undefined
            }
            className="flex-1 flex items-center justify-center gap-2 rounded-xl btn-accent px-4 py-3 text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Send className="h-4 w-4" />
            <span>Disparar agora</span>
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/* ── QuotaBar ──────────────────────────────────────────────────────────────── */

interface QuotaBarProps {
  label:     string;
  used:      number;
  limit:     number;
  willSend:  number;
  overQuota: boolean;
}

function QuotaBar({ label, used, limit, willSend, overQuota }: QuotaBarProps) {
  const usedPct      = Math.min(100, limit > 0 ? Math.round((used / limit) * 100) : 0);
  const afterSendPct = Math.min(100, limit > 0 ? Math.round(((used + willSend) / limit) * 100) : 0);

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-xs">
        <span className="text-ink-muted">{label}</span>
        <span className={`font-mono font-semibold ${overQuota ? 'text-danger' : 'text-ink-subtle'}`}>
          {used.toLocaleString('pt-BR')} / {limit.toLocaleString('pt-BR')}
        </span>
      </div>
      <div className="relative h-2 w-full rounded-full bg-midnight-4 overflow-hidden">
        {/* Already used */}
        <div
          className="absolute inset-y-0 left-0 rounded-full bg-ink-subtle/40"
          style={{ width: `${usedPct}%` }}
        />
        {/* Projected after send */}
        <div
          className={`absolute inset-y-0 left-0 rounded-full transition-all duration-500 ${
            overQuota ? 'bg-danger/70' : 'bg-accent/70'
          }`}
          style={{ width: `${afterSendPct}%` }}
        />
      </div>
      <p className="text-[10px] text-ink-subtle">
        +{willSend.toLocaleString('pt-BR')} → {(used + willSend).toLocaleString('pt-BR')} após este disparo
      </p>
    </div>
  );
}
